import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { CompetitionRubric } from '../../components/competition/RubricScoreForm';
import {
  CompetitionRole, ROLE_LABELS, RubricConfig, RubricDefinition, errorMessage, errorStatus, saveRubric,
} from '../../services/manageApi';

export const SCORES = ['1', '2', '3', '4', '5'];

export interface DraftCriterion {
  label: string;
  description: string;
  weight: string;
  guidance: Record<string, string>;
}

export interface RubricDraft {
  title: string;
  criteria: DraftCriterion[];
}

const emptyGuidance = (): Record<string, string> => ({ '1': '', '2': '', '3': '', '4': '', '5': '' });

export const SAMPLE_RUBRIC: RubricDraft = {
  title: 'Evaluation rubric',
  criteria: [
    {
      label: 'Impact',
      description: 'How meaningful is the problem, and how much does the solution help?',
      weight: '1',
      guidance: { '1': 'No clear problem or benefit', '2': 'Limited benefit', '3': 'Reasonable benefit', '4': 'Clear, well-argued benefit', '5': 'Compelling, evidenced benefit' },
    },
    {
      label: 'Technical quality',
      description: 'How sound and complete is the work?',
      weight: '1',
      guidance: { '1': 'Does not work', '2': 'Major gaps', '3': 'Works with gaps', '4': 'Solid and complete', '5': 'Robust and polished' },
    },
    {
      label: 'Presentation',
      description: 'How clearly is the work communicated?',
      weight: '1',
      guidance: { '1': 'Unclear', '2': 'Hard to follow', '3': 'Understandable', '4': 'Clear', '5': 'Compelling and clear' },
    },
  ],
};

export function newCriterion(): DraftCriterion {
  return { label: '', description: '', weight: '1', guidance: emptyGuidance() };
}

export function slugify(label: string): string {
  const slug = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // drop accents left as combining marks
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 90);
  return slug || 'criterion';
}

/** Keys from labels, made unique; the server requires unique, non-empty keys. */
export function criterionKeys(criteria: Pick<DraftCriterion, 'label'>[]): string[] {
  const used = new Set<string>();
  return criteria.map((criterion) => {
    const base = slugify(criterion.label);
    let key = base;
    let suffix = 2;
    while (used.has(key)) {
      key = `${base}_${suffix}`;
      suffix += 1;
    }
    used.add(key);
    return key;
  });
}

export function weightShares(criteria: Pick<DraftCriterion, 'weight'>[]): number[] {
  const weights = criteria.map((criterion) => {
    const weight = Number(criterion.weight);
    return Number.isFinite(weight) && weight > 0 ? weight : 0;
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  return weights.map((weight) => (total > 0 ? Math.round((weight / total) * 100) : 0));
}

export function validateRubric(draft: RubricDraft): string | null {
  const title = draft.title.trim();
  if (!title) return 'Rubric title is required.';
  if (title.length > 200) return 'Rubric title must be at most 200 characters.';
  if (draft.criteria.length === 0) return 'Add at least one criterion.';
  for (let index = 0; index < draft.criteria.length; index += 1) {
    const criterion = draft.criteria[index];
    const label = criterion.label.trim();
    if (!label) return `Criterion ${index + 1} needs a name.`;
    if (label.length > 200) return `${label}: name must be at most 200 characters.`;
    if (criterion.description.length > 2000) return `${label}: description must be at most 2000 characters.`;
    const weight = Number(criterion.weight);
    if (!Number.isFinite(weight) || weight <= 0) return `${label}: weight must be a number greater than 0.`;
    const missing = SCORES.filter((score) => !(criterion.guidance[score] || '').trim());
    if (missing.length) return `${label}: add guidance for score ${missing.join(', ')}.`;
  }
  return null;
}

export function toDefinition(draft: RubricDraft): RubricDefinition {
  const keys = criterionKeys(draft.criteria);
  return {
    title: draft.title.trim(),
    criteria: draft.criteria.map((criterion, index) => ({
      criterion_key: keys[index],
      label: criterion.label.trim(),
      description: criterion.description.trim(),
      guidance: Object.fromEntries(SCORES.map((score) => [score, (criterion.guidance[score] || '').trim()])),
      weight: Number(criterion.weight),
    })),
  };
}

export function fromRubric(rubric: CompetitionRubric): RubricDraft {
  return {
    title: rubric.title,
    criteria: rubric.criteria.map((criterion) => ({
      label: criterion.label,
      description: criterion.description || '',
      weight: String(Number(criterion.weight)),
      guidance: Object.fromEntries(SCORES.map((score) => [score, criterion.guidance?.[score] ?? ''])),
    })),
  };
}

const RubricReadOnly: React.FC<{ draft: RubricDraft }> = ({ draft }) => {
  const shares = weightShares(draft.criteria);
  return (
    <div className="rubric-readonly">
      <p><strong>{draft.title}</strong></p>
      <ul>
        {draft.criteria.map((criterion, index) => (
          <li key={`${criterion.label}-${index}`}>
            <strong>{criterion.label}</strong> ({shares[index]}%)
            {criterion.description && <> — {criterion.description}</>}
            <ol>
              {SCORES.map((score) => <li key={score}>{criterion.guidance[score]}</li>)}
            </ol>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface Props {
  competitionId: number;
  config: RubricConfig;
  canEdit: boolean;
  requiredRole?: CompetitionRole;
  onSaved: (config: RubricConfig) => void;
  onDirtyChange: (section: string, dirty: boolean) => void;
}

const SECTION = 'rubric';

const RubricEditor: React.FC<Props> = ({ competitionId, config, canEdit, requiredRole, onSaved, onDirtyChange }) => {
  const baseline = useMemo(() => (config.rubric ? fromRubric(config.rubric) : null), [config.rubric]);
  const [draft, setDraft] = useState<RubricDraft | null>(baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    setDraft(baseline);
  }, [baseline]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

  useEffect(() => {
    onDirtyChange(SECTION, dirty);
  }, [dirty, onDirtyChange]);

  const locked = Boolean(config.locked);
  const editable = canEdit && !locked;
  const shares = useMemo(() => (draft ? weightShares(draft.criteria) : []), [draft]);

  const updateCriterion = (index: number, patch: Partial<DraftCriterion>) => {
    setDraft((prev) => prev && { ...prev, criteria: prev.criteria.map((c, i) => (i === index ? { ...c, ...patch } : c)) });
  };
  const updateGuidance = (index: number, score: string, text: string) => {
    setDraft((prev) => prev && {
      ...prev,
      criteria: prev.criteria.map((c, i) => (i === index ? { ...c, guidance: { ...c.guidance, [score]: text } } : c)),
    });
  };
  const move = (index: number, delta: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const target = index + delta;
      if (target < 0 || target >= prev.criteria.length) return prev;
      const criteria = [...prev.criteria];
      [criteria[index], criteria[target]] = [criteria[target], criteria[index]];
      return { ...prev, criteria };
    });
  };
  const remove = (index: number) => setDraft((prev) => prev && { ...prev, criteria: prev.criteria.filter((_, i) => i !== index) });
  const add = () => setDraft((prev) => prev && { ...prev, criteria: [...prev.criteria, newCriterion()] });

  const save = async () => {
    if (!draft) return;
    const problem = validateRubric(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await saveRubric(competitionId, toDefinition(draft));
      onSaved({ evaluation_mode: 'rubric', rubric: result.rubric, locked: false });
      setToast('Rubric saved');
    } catch (err) {
      setError(errorMessage(err, 'Failed to save rubric'));
      if (errorStatus(err) === 409) {
        // Judging started meanwhile: drop the rejected edit and show the locked view.
        setDraft(baseline);
        onSaved({ ...config, locked: true });
      }
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (!draft) {
    body = editable ? (
      <>
        <p>No rubric yet. Judges give one overall score from 1 to 100.</p>
        <Button variant="primary" size="sm" onClick={() => setDraft(JSON.parse(JSON.stringify(SAMPLE_RUBRIC)))}>
          Create rubric
        </Button>
      </>
    ) : (
      <p>No rubric configured.</p>
    );
  } else if (!editable) {
    body = <RubricReadOnly draft={draft} />;
  } else {
    body = (
      <>
        <div className="manage-field">
          <label htmlFor="rubric-title" className="manage-field__label">Rubric title</label>
          <input
            id="rubric-title"
            className="input input--default input--md"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            disabled={saving}
          />
        </div>
        {draft.criteria.map((criterion, index) => (
          <fieldset key={index} className="rubric-card">
            <legend>Criterion {index + 1}</legend>
            <div className="rubric-card__row">
              <label>
                Name
                <input
                  className="input input--default input--md"
                  aria-label={`Criterion ${index + 1} name`}
                  value={criterion.label}
                  onChange={(e) => updateCriterion(index, { label: e.target.value })}
                  disabled={saving}
                />
              </label>
              <label>
                Weight
                <input
                  className="input input--default input--md"
                  type="number"
                  min="0"
                  step="any"
                  aria-label={`Criterion ${index + 1} weight`}
                  value={criterion.weight}
                  onChange={(e) => updateCriterion(index, { weight: e.target.value })}
                  disabled={saving}
                />
                <span className="rubric-card__share">→ {shares[index]}%</span>
              </label>
            </div>
            <label>
              Description
              <textarea
                className="input input--default input--md"
                rows={2}
                aria-label={`Criterion ${index + 1} description`}
                value={criterion.description}
                onChange={(e) => updateCriterion(index, { description: e.target.value })}
                disabled={saving}
              />
            </label>
            <div className="rubric-card__guidance">
              {SCORES.map((score) => (
                <label key={score}>
                  Score {score}
                  <textarea
                    className="input input--default input--md"
                    rows={2}
                    aria-label={`Criterion ${index + 1} guidance for score ${score}`}
                    value={criterion.guidance[score] || ''}
                    onChange={(e) => updateGuidance(index, score, e.target.value)}
                    disabled={saving}
                  />
                </label>
              ))}
            </div>
            <div className="rubric-card__tools">
              <Button variant="ghost" size="sm" disabled={saving || index === 0} onClick={() => move(index, -1)}>Move up</Button>
              <Button variant="ghost" size="sm" disabled={saving || index === draft.criteria.length - 1} onClick={() => move(index, 1)}>Move down</Button>
              <Button variant="danger" size="sm" disabled={saving || draft.criteria.length === 1} onClick={() => remove(index)}>Remove</Button>
            </div>
          </fieldset>
        ))}
        <Button variant="outline" size="sm" disabled={saving} onClick={add}>+ Add criterion</Button>
        {error && <p className="manage-error" role="alert">{error}</p>}
        <div className="manage-actions">
          <Button variant="secondary" disabled={!dirty || saving} onClick={() => { setDraft(baseline); setError(''); }}>
            Discard
          </Button>
          <Button variant="primary" loading={saving} disabled={!dirty || saving} onClick={save}>
            Save rubric
          </Button>
        </div>
      </>
    );
  }

  return (
    <section className="manage-section" data-tour="manage-rubric">
      <h3>
        Rubric
        {!canEdit && requiredRole && <span className="manage-field__lock"> 🔒 {ROLE_LABELS[requiredRole]}</span>}
      </h3>
      <p className="manage-field__help">Judges score each criterion from 1 to 5. The overall score is the weighted average.</p>
      {locked && (
        <div className="manage-banner" role="status">
          🔒 Rubric is locked because judging has started. Every judge must use the same rubric for scores to be comparable.
        </div>
      )}
      {body}
      {toast && <Toast message={toast} duration={2500} onClose={() => setToast('')} />}
    </section>
  );
};

export default RubricEditor;
